/* ═══════════════════════════════════════════════════════
   SwipeHome — UI Utilities Module
   Shared UI helpers: modal, toast, escapeHtml, formatters,
   media helpers, ListControls, role utilities
   ═══════════════════════════════════════════════════════ */

import { TOAST_DURATION_MS, TOAST_ERROR_DURATION_MS } from './state.js';
import type { Listing, ListingMedia } from '../../shared/types.js';
import type { Screen } from './types/ui.js';

// ── Forward-declared references — set by main.ts to avoid circular imports ──
type NavigateFn = (screen: Screen, data?: Record<string, unknown>) => void;
type RenderFn = () => void;
type RenderTabBarFn = (tabBar: HTMLElement) => void;

let _navigate: NavigateFn | null = null;
let _render: RenderFn | null = null;
let _renderTabBar: RenderTabBarFn | null = null;

export function setUiNavigate(fn: NavigateFn): void {
  _navigate = fn;
}
export function setUiRender(fn: RenderFn): void {
  _render = fn;
}
export function setUiRenderTabBar(fn: RenderTabBarFn): void {
  _renderTabBar = fn;
}

// Internal helper to satisfy noUnusedLocals; these are read by other
// modules via the window globals or the setters above.
void _navigate;
void _render;
void _renderTabBar;

// ─── Utility ───────────────────────────────────────────

/**
 * Feature 11 — compact "Open Sat 2–4pm" label for open-house
 * badges on cards / pins / detail. Returns empty string when the
 * event is missing or malformed so callers can trivially skip.
 */
export function formatOpenHouseLabel(
  oh: { event_date: string; start_time: string | null; end_time: string | null } | null | undefined,
): string {
  if (!oh || !oh.event_date) return '';
  // Parse as local date — input is "YYYY-MM-DD", no timezone to fight.
  const parts = oh.event_date.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '';
  const d = new Date(parts[0]!, parts[1]! - 1, parts[2]!);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let dayLabel: string;
  if (diffDays === 0) dayLabel = 'Today';
  else if (diffDays === 1) dayLabel = 'Tomorrow';
  else if (diffDays > 1 && diffDays < 7) dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' });
  else dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const fmtTime = (t: string | null): string => {
    if (!t) return '';
    const tp = t.split(':');
    let h = parseInt(tp[0] || '0', 10);
    const m = parseInt(tp[1] || '0', 10);
    if (Number.isNaN(h)) return '';
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return m === 0 ? `${h}${ampm}` : `${h}:${String(m).padStart(2, '0')}${ampm}`;
  };

  const start = fmtTime(oh.start_time);
  const end = fmtTime(oh.end_time);
  let timeLabel = '';
  if (start && end) {
    // Drop the am/pm suffix on the start if same period as end (e.g. "2–4pm")
    const startPeriod = start.match(/(am|pm)$/)?.[1];
    const endPeriod = end.match(/(am|pm)$/)?.[1];
    if (startPeriod && startPeriod === endPeriod) {
      timeLabel = `${start.replace(/(am|pm)$/, '')}\u2013${end}`;
    } else {
      timeLabel = `${start}\u2013${end}`;
    }
  } else if (start) {
    timeLabel = start;
  }

  return timeLabel ? `${dayLabel} \u00B7 ${timeLabel}` : dayLabel;
}


export function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function getInitials(name: string | null | undefined): string {
  return (name || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface AddressParts {
  address_line1?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  zip_code?: string | null;
}

/**
 * Compose a one-line listing address without duplicating locality.
 * Some sources already embed ", City[, ST ZIP]" in address_line1 —
 * appending city/state again would render "Houston, Houston, TX".
 * Only locality components missing from address_line1 are appended.
 */
export function formatListingAddress(listing: AddressParts | null | undefined): string {
  const rawLine1 = listing?.address_line1 || (listing?.address || '').split(',')[0] || '';
  const city = (listing?.city || '').trim();
  const st = (listing?.state || '').trim();
  const zip = (listing?.zip || listing?.zip_code || '').trim();
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let addr1 = rawLine1.trim();
  const tail = [city, [st, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  if (addr1 && tail) {
    // Strip a trailing locality that address_line1 already contains.
    // Sources sometimes embed a *different* city than the structured city
    // field (e.g. line1 "6011 Broadway #5E, Woodside, NY 11377" with city
    // "Flushing") — the structured city/state/ZIP wins so the final line
    // never repeats "NY 11377" or stacks two competing localities.
    const stateZip = [st, zip].filter(Boolean).join(' ');
    const full = [city, stateZip].filter(Boolean).join(', ');
    const patterns: RegExp[] = [];
    if (full) patterns.push(new RegExp(`,\\s*${esc(full)}\\s*$`, 'i'));
    if (city) patterns.push(new RegExp(`,\\s*${esc(city)}\\s*$`, 'i'));
    // ", <SomeCity>, ST [ZIP]" with any state/ZIP — the locality token
    // must be letter-led with no digits, so unit designators like
    // "Apt 4B" are never stripped.
    patterns.push(new RegExp(`,\\s*[A-Za-z][A-Za-z .'\u2019-]*,\\s*[A-Za-z]{2}(?:\\s+\\d{5}(?:-\\d{4})?)?\\s*$`, 'i'));
    // Bare trailing ", ST ZIP" with no city token at all.
    patterns.push(new RegExp(`,\\s*[A-Za-z]{2}\\s+\\d{5}(?:-\\d{4})?\\s*$`, 'i'));
    for (const p of patterns) addr1 = addr1.replace(p, '').trim();
  }
  if (!tail) return addr1;
  return addr1 ? `${addr1}, ${tail}` : tail;
}

// Consumer-facing agreement type label. Backend stores raw enums like
// `exclusive_buyer` (the buyer/agent representation contract type) — the
// naive `.replace(/_/g, ' ')` render surfaces literal "exclusive buyer"
// which reads oddly on the investor surface. Map to representation-first
// language for consumers; agent surfaces (which show "Exclusive Buyer
// Representation" in dropdowns) are unaffected.
const _AGREEMENT_TYPE_LABELS: Record<string, string> = {
  exclusive_buyer: 'exclusive representation',
  non_exclusive: 'non-exclusive representation',
  touring_only: 'touring only',
};

export function formatAgreementType(raw: string | null | undefined): string {
  if (!raw) return '';
  return _AGREEMENT_TYPE_LABELS[raw] || raw.replace(/_/g, ' ');
}

// ─── ListControls ─────────────────────────────────────────────────────

export interface ListControlsFilterSelectOption {
  value: string;
  label: string;
}

export type ListControlsFilterOption = string | ListControlsFilterSelectOption;

export interface ListControlsFilter {
  key: string;
  label: string;
  type: 'select' | 'price' | 'number';
  options?: ListControlsFilterOption[];
}

export interface ListControlsSortOption {
  value: string;
  label: string;
}

export interface ListControlsConfig {
  container: HTMLElement | null;
  paginationContainer?: HTMLElement | null;
  onUpdate: (params: Record<string, string | number>) => void;
  searchPlaceholder?: string;
  filters?: ListControlsFilter[];
  sortOptions?: ListControlsSortOption[];
  defaultSort?: string | null;
  defaultPerPage?: number;
}

interface ListControlsParams {
  page: number;
  per_page: number;
  search: string | null;
  sort_by: string | null;
  sort_dir: 'asc' | 'desc';
  [key: string]: string | number | null;
}

interface PaginationInfo {
  page: number;
  total_pages: number;
  total_items: number;
  has_prev: boolean;
  has_next: boolean;
}

export class ListControls {
  container: HTMLElement | null;
  paginationContainer: HTMLElement | null;
  onUpdate: (params: Record<string, string | number>) => void;
  searchPlaceholder: string;
  filters: ListControlsFilter[];
  sortOptions: ListControlsSortOption[];
  defaultSort: string | null;
  defaultPerPage: number;
  params: ListControlsParams;
  private _debounce: ReturnType<typeof setTimeout> | null;

  constructor(config: ListControlsConfig) {
    this.container = config.container;
    this.paginationContainer = config.paginationContainer ?? null;
    this.onUpdate = config.onUpdate;
    this.searchPlaceholder = config.searchPlaceholder || 'Search...';
    this.filters = config.filters || [];
    this.sortOptions = config.sortOptions || [];
    this.defaultSort = config.defaultSort || null;
    this.defaultPerPage = config.defaultPerPage || 12;
    this.params = {
      page: 1,
      per_page: this.defaultPerPage,
      search: null,
      sort_by: this.defaultSort,
      sort_dir: 'desc',
    };
    this.filters.forEach((f) => {
      this.params[f.key] = null;
    });
    this._debounce = null;
    this.render();
    this.triggerUpdate();
  }

  render(): void {
    if (!this.container) return;
    this.container.innerHTML = '';
    this.container.className = 'list-controls';

    // Search row
    const searchRow = document.createElement('div');
    searchRow.className = 'list-controls-search-row';
    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.className = 'list-search-input';
    searchInput.placeholder = this.searchPlaceholder;
    if (this.params.search) searchInput.value = this.params.search;
    searchInput.addEventListener('input', (e) => {
      if (this._debounce) clearTimeout(this._debounce);
      this._debounce = setTimeout(() => {
        const target = e.target as HTMLInputElement;
        this.params.search = target.value.trim() || null;
        this.params.page = 1;
        this.triggerUpdate();
      }, 400);
    });
    searchRow.appendChild(searchInput);

    if (this.filters.length > 0) {
      const filterBtn = document.createElement('button');
      filterBtn.className = 'list-filter-toggle';
      filterBtn.innerHTML =
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>';
      filterBtn.addEventListener('click', () => {
        const panel = this.container?.querySelector('.list-filter-panel');
        if (panel) {
          panel.classList.toggle('hidden');
          filterBtn.classList.toggle('active');
        }
      });
      searchRow.appendChild(filterBtn);
    }
    this.container.appendChild(searchRow);

    // Filter panel
    if (this.filters.length > 0) {
      const panel = document.createElement('div');
      panel.className = 'list-filter-panel hidden';
      this.filters.forEach((f) => {
        const group = document.createElement('div');
        group.className = 'list-filter-group';
        const label = document.createElement('label');
        label.className = 'list-filter-label';
        label.textContent = f.label;
        group.appendChild(label);

        if (f.type === 'select') {
          const sel = document.createElement('select');
          sel.className = 'list-filter-select';
          (f.options || []).forEach((opt) => {
            const o = document.createElement('option');
            const optionValue = typeof opt === 'string' ? opt : opt.value;
            const lbl = typeof opt === 'string' ? opt : opt.label;
            o.value = optionValue === 'All' || optionValue === 'Any' ? '' : optionValue.toLowerCase();
            o.textContent = lbl;
            if (this.params[f.key] === o.value || (!this.params[f.key] && !o.value)) o.selected = true;
            sel.appendChild(o);
          });
          sel.addEventListener('change', (e) => {
            const target = e.target as HTMLSelectElement;
            this.params[f.key] = target.value || null;
            this.params.page = 1;
            this.triggerUpdate();
          });
          group.appendChild(sel);
        } else if (f.type === 'price' || f.type === 'number') {
          const inp = document.createElement('input');
          inp.type = 'number';
          inp.className = 'list-filter-input';
          inp.placeholder = f.label;
          if (this.params[f.key]) inp.value = String(this.params[f.key]);
          inp.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            this.params[f.key] = target.value ? parseInt(target.value, 10) : null;
            this.params.page = 1;
            this.triggerUpdate();
          });
          group.appendChild(inp);
        }
        panel.appendChild(group);
      });
      this.container.appendChild(panel);
    }

    // Sort row
    if (this.sortOptions.length > 0) {
      const sortRow = document.createElement('div');
      sortRow.className = 'list-controls-sort-row';
      const sortLabel = document.createElement('span');
      sortLabel.className = 'list-sort-label';
      sortLabel.textContent = 'Sort by:';
      sortRow.appendChild(sortLabel);

      const sortSel = document.createElement('select');
      sortSel.className = 'list-sort-select';
      this.sortOptions.forEach((opt) => {
        const o = document.createElement('option');
        o.value = opt.value;
        o.textContent = opt.label;
        if (opt.value === (this.params.sort_by || this.defaultSort)) o.selected = true;
        sortSel.appendChild(o);
      });
      sortSel.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        this.params.sort_by = target.value;
        this.params.page = 1;
        this.triggerUpdate();
      });
      sortRow.appendChild(sortSel);

      const dirBtn = document.createElement('button');
      dirBtn.className = 'list-sort-dir';
      dirBtn.textContent = this.params.sort_dir === 'desc' ? '\u2193' : '\u2191';
      dirBtn.title = 'Toggle sort direction';
      dirBtn.addEventListener('click', () => {
        this.params.sort_dir = this.params.sort_dir === 'desc' ? 'asc' : 'desc';
        dirBtn.textContent = this.params.sort_dir === 'desc' ? '\u2193' : '\u2191';
        this.triggerUpdate();
      });
      sortRow.appendChild(dirBtn);
      this.container.appendChild(sortRow);
    }
  }

  updatePagination(pg: PaginationInfo | null | undefined): void {
    if (!this.paginationContainer || !pg) return;
    const { page, total_pages, total_items, has_prev, has_next } = pg;
    if (total_pages <= 1) {
      this.paginationContainer.innerHTML = `<div class="pagination-info">${total_items} item${total_items !== 1 ? 's' : ''}</div>`;
      return;
    }
    const pages = this._pageNums(page, total_pages);
    this.paginationContainer.innerHTML = `
      <div class="pagination-controls">
        <button class="page-btn ${has_prev ? '' : 'disabled'}" data-page="${page - 1}" ${!has_prev ? 'disabled' : ''}>\u2039 Prev</button>
        <div class="page-numbers">
          ${pages.map((p) => (p === '...' ? '<span class="page-ellipsis">\u2026</span>' : `<button class="page-num ${p === page ? 'active' : ''}" data-page="${p}">${p}</button>`)).join('')}
        </div>
        <button class="page-btn ${has_next ? '' : 'disabled'}" data-page="${page + 1}" ${!has_next ? 'disabled' : ''}>Next \u203a</button>
      </div>
      <div class="pagination-info">Page ${page} of ${total_pages} \u00b7 ${total_items} items</div>
    `;
    this.paginationContainer.querySelectorAll<HTMLElement>('[data-page]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const currentTarget = e.currentTarget as HTMLElement;
        const p = parseInt(currentTarget.dataset.page || '0', 10);
        if (p >= 1 && p <= total_pages && p !== page) {
          this.params.page = p;
          this.triggerUpdate();
          this.container?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  private _pageNums(cur: number, total: number): (number | '...')[] {
    if (window.innerWidth < 360 || total <= 1) return [];
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const p: (number | '...')[] = [1];
    if (cur > 3) p.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) p.push(i);
    if (cur < total - 2) p.push('...');
    p.push(total);
    return p;
  }

  triggerUpdate(): void {
    const clean: Record<string, string | number> = {};
    Object.entries(this.params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') clean[k] = v;
    });
    this.onUpdate(clean);
  }

  getQueryString(): string {
    const clean: Record<string, string> = {};
    Object.entries(this.params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') clean[k] = String(v);
    });
    return new URLSearchParams(clean).toString();
  }
}

// ─── Media helpers ────────────────────────────────────────────────────

interface MediaContainer {
  media?: ListingMedia[] | string | null;
  listing_media?: ListingMedia[] | string | null;
  photos?: (string | { url?: string | null })[] | null;
  photo_url?: string | null;
}

export function getMediaUrl(listing: MediaContainer | null | undefined): string {
  if (!listing) return '';
  let media: ListingMedia[] | string | null | undefined = listing.media ?? listing.listing_media;
  if (typeof media === 'string') {
    try {
      media = JSON.parse(media);
    } catch {
      return '';
    }
  }
  if (Array.isArray(media) && media.length > 0) {
    const sorted = [...media].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return normalizePhotoUrl(sorted[0]?.url || '');
  }
  if (Array.isArray(listing.photos) && listing.photos.length > 0) {
    const first = listing.photos[0];
    const raw = typeof first === 'string' ? first : first?.url || '';
    return normalizePhotoUrl(raw);
  }
  if (listing.photo_url) return normalizePhotoUrl(listing.photo_url);
  return '';
}

export function normalizePhotoUrl(url: string | null | undefined): string {
  if (!url) return '';
  let u = url.trim();
  if (u.startsWith('//')) u = 'https:' + u;
  if (!u.startsWith('http')) return '';
  return u;
}

// ─── Branded "Photo unavailable" placeholder ────────────────────────────
// Used by imgFallback (onerror handler) and renderListingPhoto (null-url
// branch) so every slot that should hold a listing photo either shows a
// real image or the same branded placeholder — never a broken-image icon,
// never a blank gray box.
const PLACEHOLDER_SVG = '<svg class="img-placeholder-icon" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true"><path d="M20 6 L6 18 L6 34 L14 34 L14 24 L26 24 L26 34 L34 34 L34 18 Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/><circle cx="29" cy="14" r="1.5" fill="currentColor"/></svg>';

const PLACEHOLDER_INNER = `<div class="img-placeholder-inner">${PLACEHOLDER_SVG}<div class="img-placeholder-text">Photo unavailable</div></div>`;

export function imgFallback(el: HTMLImageElement): void {
  el.onerror = null;
  el.onload = null;
  // Hide the broken img and mark the parent so the fallback reads as
  // "this slot lost its photo" rather than leaving a visual gap.
  el.style.display = 'none';
  const parent = el.parentElement;
  if (!parent) return;
  // Avoid double-injecting if fallback fires more than once (some browsers
  // fire onerror repeatedly on retries).
  if (parent.querySelector('.img-placeholder')) return;
  const ph = document.createElement('div');
  // Copy the img's classes onto the placeholder so size variants
  // (.swipe-card-image, .listing-item-image, .my-listing-image,
  // .map-card-photo, etc.) constrain it to the same box the image
  // was occupying.
  ph.className = `img-placeholder img-placeholder-branded ${el.className}`.replace(/\bimg-loading\b/g, '').trim();
  ph.innerHTML = PLACEHOLDER_INNER;
  // Insert the placeholder where the original img lived (before it)
  // rather than appendChild, which would dump it at the end of the
  // parent and reorder flex siblings. Fix surfaced by 4e map-drawer
  // cards but benefits every card layout that has siblings after
  // the image (listing-item in Likes, map-card in drawer, etc.).
  parent.insertBefore(ph, el);
}

/**
 * Render the HTML for a listing photo slot. Handles three states with one
 * helper so every call site looks the same:
 *
 *   1. `url` is a usable string → `<img>` with shimmer-while-loading + the
 *      standard onerror fallback.
 *   2. `url` is null/empty/garbage → a branded "Photo unavailable"
 *      placeholder with the same CSS class (so it fits the same box).
 *   3. The img loads and fires `onload` → the `.loaded` class is added,
 *      which CSS uses to kill the shimmer background.
 *
 * `cssClass` should be the existing size class (swipe-card-image,
 * listing-item-image, my-listing-image, etc.) so the slot keeps its
 * dimensions.
 */
export function renderListingPhoto(
  url: string | null | undefined,
  cssClass: string,
  altText: string = 'Home photo',
  extraAttrs: string = '',
): string {
  const clean = normalizePhotoUrl(url);
  if (!clean) {
    return `<div class="${cssClass} img-placeholder img-placeholder-branded" ${extraAttrs}>${PLACEHOLDER_INNER}</div>`;
  }
  return `<img class="${cssClass} img-loading" src="${escapeHtml(clean)}" alt="${escapeHtml(altText)}" loading="lazy" onload="this.classList.add('loaded')" onerror="imgFallback(this)" ${extraAttrs}>`;
}

interface MediaLike {
  url: string;
  sort_order: number;
}

export function getAllMedia(listing: MediaContainer | null | undefined): MediaLike[] {
  if (!listing) return [];
  let media: ListingMedia[] | string | null | undefined = listing.media ?? listing.listing_media;
  if (typeof media === 'string') {
    try {
      media = JSON.parse(media);
    } catch {
      return [];
    }
  }
  if (Array.isArray(media) && media.length > 0) {
    return [...media]
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((m) => ({ url: m.url, sort_order: m.sort_order }));
  }
  if (Array.isArray(listing.photos) && listing.photos.length > 0) {
    return listing.photos.map((p, i) => ({
      url: typeof p === 'string' ? p : p?.url || '',
      sort_order: i,
    }));
  }
  return [];
}

// ─── Toast ─────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export function showToast(message: string, type: ToastType = 'info'): void {
  const container = document.getElementById('toast-container');
  if (!container) return;
  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.setAttribute('role', 'alert');
  const icons: Record<ToastType, string> = {
    success: '&#10003;',
    error: '&#10007;',
    info: '&#8505;',
    warning: '&#9888;',
  };
  const icon = icons[type];
  const safeMsg = escapeHtml(String(message || ''));
  toast.innerHTML = `<span style="font-size:16px;line-height:1">${icon}</span><span style="flex:1">${safeMsg}</span><button onclick="this.parentElement.remove()" style="background:none;border:none;color:inherit;cursor:pointer;padding:2px 4px;font-size:16px;opacity:0.7">&times;</button>`;
  container.appendChild(toast);
  const duration = type === 'error' ? TOAST_ERROR_DURATION_MS : TOAST_DURATION_MS;
  setTimeout(() => {
    if (toast.isConnected) toast.remove();
  }, duration);
}

// ─── Escape + password toggle ─────────────────────────────────────────

export function escapeHtml(str: string | number | null | undefined): string {
  const div = document.createElement('div');
  div.textContent = str == null ? '' : String(str);
  return div.innerHTML;
}

export function togglePasswordVisibility(inputId: string, btn: HTMLButtonElement): void {
  const input = document.getElementById(inputId) as HTMLInputElement | null;
  if (!input) return;
  const isPassword = input.type === 'password';
  input.type = isPassword ? 'text' : 'password';
  const icon = btn.querySelector('i, svg');
  if (icon) {
    icon.setAttribute('data-feather', isPassword ? 'eye' : 'eye-off');
    if (window.feather) window.feather.replace();
  }
}

interface UserNameLike {
  full_name?: string;
  first_name?: string;
  last_name?: string;
}

export function getUserName(user: UserNameLike | null | undefined): string {
  if (!user) return '';
  if (user.full_name) return user.full_name;
  return ((user.first_name || '') + ' ' + (user.last_name || '')).trim();
}


// ─── Modal ─────────────────────────────────────────────────────────────

export interface ModalButton {
  label: string;
  style?: 'primary' | 'danger' | 'secondary';
  onclick?: () => void;
}

export function closeModal(): void {
  const modal = document.getElementById('modal-container');
  if (!modal) return;
  modal.classList.add('hidden');
  modal.innerHTML = '';
  document.body.style.overflow = '';
}

export function openModal(title: string, bodyHtml: string, buttons?: ModalButton[]): void {
  const modal = document.getElementById('modal-container');
  if (!modal) return;
  document.body.style.overflow = 'hidden';
  const btnHtml = (buttons || [])
    .map((btn) => {
      const bg = btn.style === 'primary' ? 'var(--primary)' : btn.style === 'danger' ? '#ff4458' : 'transparent';
      const border = btn.style === 'primary' ? 'var(--primary)' : btn.style === 'danger' ? '#ff4458' : '#2a2a4a';
      const color = btn.style === 'secondary' ? '#9ca3af' : '#fff';
      return `<button class="modal-action-btn" style="flex:1;padding:12px;border-radius:10px;border:1px solid ${border};background:${bg};color:${color};font-size:14px;font-weight:600;font-family:var(--font-body);cursor:pointer" id="modal-btn-${btn.label.replace(/\s/g, '')}">${escapeHtml(btn.label)}</button>`;
    })
    .join('');
  modal.innerHTML = `
    <div style="position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:1000;display:flex;align-items:flex-end;justify-content:center" onclick="closeModal()">
      <div style="width:100%;max-width:440px;background:#1a1a2e;border-radius:20px 20px 0 0;padding:24px 20px 32px;max-height:85vh;overflow-y:auto" onclick="event.stopPropagation()">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
          <h3 style="font-size:18px;font-weight:700;color:#fff;margin:0">${escapeHtml(title)}</h3>
          <button onclick="closeModal()" style="background:none;border:none;color:#9ca3af;cursor:pointer;font-size:20px;padding:4px">\u2715</button>
        </div>
        <div>${bodyHtml}</div>
        ${btnHtml ? `<div style="display:flex;gap:10px;margin-top:20px">${btnHtml}</div>` : ''}
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
  (buttons || []).forEach((btn) => {
    const el = document.getElementById('modal-btn-' + btn.label.replace(/\s/g, ''));
    if (el && btn.onclick) el.addEventListener('click', btn.onclick);
  });
}

// Marker to satisfy isolatedModules; re-export a listing type for
// downstream consumers that need typed media helpers.
export type { Listing };
